import React from 'react';
import { Chip } from '@material-ui/core';

import { tokens, withAlpha } from '../../core/theming/tokens';

// ── Niveles de membresía ────────────────────────────────────────────────────
// Espejo del composer (gamification.service): basic/premium/elite por puntos +
// 'ultra' por tierOverride (cuenta especial). El boost que COBRA el robot:
//   ultra → 0 (no usa boost; cobra el APR negociado por moneda, campo ultraApr) ·
//   elite → eliteBoostPct (def 50) · premium → premiumBoostPct (def 25) · basic → 0.
export function tierMeta(tier?: string): { label: string; color: string } {
  switch (tier) {
    case 'ultra':
      return { label: 'Ultra · VIP', color: tokens.tierUltra };
    case 'elite':
      return { label: 'Elite', color: tokens.tierElite };
    case 'premium':
      return { label: 'Premium', color: tokens.tierPremium };
    default:
      return { label: 'Básico', color: tokens.tierBasic };
  }
}

// Boost por nivel. Los % de Premium/Elite son CONFIGURABLES en Parámetros
// (settings/gamification: premiumBoostPct / eliteBoostPct); estos son los valores
// por defecto, que se usan mientras el doc no cargó o no define el campo. Ultra NO
// tiene boost: su rendimiento sale del APR negociado por moneda (ultraApr).
export const BOOST_DEFAULTS = { premium: 25, elite: 50 };

export function boostPctFor(
  tier?: string,
  pcts: { premium: number; elite: number } = BOOST_DEFAULTS
): number {
  switch (tier) {
    case 'ultra':
      return 0;
    case 'elite':
      return pcts.elite;
    case 'premium':
      return pcts.premium;
    default:
      return 0;
  }
}

// ── Puntos (cache diario del robot) ─────────────────────────────────────────
// El campo `points` (y `tier`) del doc de usuario es un CACHE que el composer
// recalcula UNA vez por día en el snapshot (misma fórmula que la web:
// capital × tasa + permanencia + referidos). Si el campo todavía no existe
// (usuario nuevo o base recién migrada, antes de la primera corrida del robot),
// se muestra un estado "sin calcular" en vez de mentir con 0 / Básico.
export const POINTS_CACHE_HINT =
  // fase51 — el composer (fase41) recalcula puntos/nivel AL MOMENTO tras cada
  // movimiento liquidado, además del snapshot diario de respaldo.
  'El robot recalcula puntos y nivel tras cada movimiento liquidado (y cada noche en el snapshot). ' +
  'Las cuentas nuevas o recién migradas figuran sin calcular hasta su primer movimiento o la primera corrida nocturna.';

export function hasPointsCache(record: any): boolean {
  return record?.points !== undefined && record?.points !== null;
}

// ── Nivel EFECTIVO (fase52) ──────────────────────────────────────────────────
// Reportado con captura: el toggle "Cuenta Ultra" estaba APAGADO y el dossier
// mostraba igual "Ultra · VIP" con 16.624 puntos (que por umbrales es Elite).
// La causa: los tres renders del nivel hacían `record.tierOverride ||
// record.tier` — al apagar el toggle, tierOverride queda null y GANA EL CACHÉ
// VIEJO (`tier: 'ultra'`), que nadie recalcula hasta el próximo movimiento del
// usuario o el snapshot nocturno. Un admin sin movimientos podía quedar "Ultra"
// para siempre.
//
// La regla correcta, y la ÚNICA que el BO usa ahora para mostrar nivel: el
// override manda; sin override, el nivel SE DERIVA DE LOS PUNTOS del caché con
// los umbrales reales (settings/gamification; defaults como respaldo). Es la
// misma cuenta que hacen la web y el robot (computeTier) — el caché `tier`
// crudo ya no se muestra jamás: puede estar huérfano.
export const TIER_THRESHOLD_DEFAULTS = { premiumMinPoints: 2000, eliteMinPoints: 10000 };

export function effectiveTier(
  record: any,
  thresholds: { premiumMinPoints: number; eliteMinPoints: number } = TIER_THRESHOLD_DEFAULTS
): string | undefined {
  if (record?.tierOverride === 'ultra') return 'ultra';
  if (!hasPointsCache(record)) return record?.tier; // sin caché: no hay de dónde derivar
  const points = Number(record.points ?? 0);
  if (points >= thresholds.eliteMinPoints) return 'elite';
  if (points >= thresholds.premiumMinPoints) return 'premium';
  return 'basic';
}

/** Umbrales desde el doc de Parámetros (o defaults si no cargó / no define). */
export function tierThresholds(gamification: any): { premiumMinPoints: number; eliteMinPoints: number } {
  const n = (v: unknown, d: number) => (Number.isFinite(Number(v)) ? Number(v) : d);
  return {
    premiumMinPoints: n(gamification?.premiumMinPoints, TIER_THRESHOLD_DEFAULTS.premiumMinPoints),
    eliteMinPoints: n(gamification?.eliteMinPoints, TIER_THRESHOLD_DEFAULTS.eliteMinPoints),
  };
}

export function formatPoints(n: any): string {
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(
    Number(n ?? 0)
  );
}

// Gema del nivel: rombo en el color del tier. Identidad de membresía.
export function Gem({ tier, size = 12 }: { tier?: string; size?: number }) {
  let { color } = tierMeta(tier);
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden>
      <rect
        x="6"
        y="0.5"
        width="7.78"
        height="7.78"
        rx="1.4"
        transform="rotate(45 6 0.5)"
        fill={withAlpha(color, 0.22)}
        stroke={color}
        strokeWidth="1.1"
      />
    </svg>
  );
}

// `unknown` = el robot todavía no cacheó puntos/nivel (ver POINTS_CACHE_HINT):
// se muestra un chip neutro "Sin calcular" en vez de un "Básico" que puede ser falso.
export function TierBadge({ tier, unknown }: { tier?: string; unknown?: boolean }) {
  if (unknown && !tier) {
    return (
      <Chip
        size="small"
        label="Sin calcular"
        title={POINTS_CACHE_HINT}
        style={{
          color: tokens.faint,
          backgroundColor: withAlpha(tokens.faint, 0.1),
          border: `1px dashed ${withAlpha(tokens.faint, 0.4)}`,
          fontWeight: 600,
        }}
      />
    );
  }
  let { label, color } = tierMeta(tier);
  return (
    <Chip
      size="small"
      icon={<span style={{ display: 'flex', marginLeft: 8 }}><Gem tier={tier} /></span>}
      label={label}
      style={{
        color,
        backgroundColor: withAlpha(color, 0.13),
        border: `1px solid ${withAlpha(color, 0.3)}`,
        fontWeight: 600,
      }}
    />
  );
}

// ── Estado de cuenta (modelo de TRES estados, espejo de la web/composer) ────
// 'admin_suspended' (histórico 'closed'): suspensión administrativa desde el BO —
//   bloquea login, operaciones y FRENA intereses. La levanta solo el admin.
// 'user_suspended' (histórico 'suspended'): el USUARIO frenó su propia cuenta
//   (freno de pánico) — no puede operar, pero SÍ puede loguear y los intereses
//   SIGUEN acreditando; se rehabilita él mismo (24 h + cambio de contraseña).
// Ausente / otro = activa.
export function isClosed(record: any): boolean {
  let s = record?.accountStatus;
  return s === 'admin_suspended' || s === 'closed';
}

export function isUserSuspended(record: any): boolean {
  let s = record?.accountStatus;
  return s === 'user_suspended' || s === 'suspended';
}

export function StatusBadge({ record }: { record: any }) {
  let label = 'Activa';
  let color: string = tokens.success;
  if (isClosed(record)) {
    label = 'Suspendida';
    color = tokens.error;
  } else if (isUserSuspended(record)) {
    label = 'Suspendida (usuario)';
    color = tokens.pending;
  }
  return (
    <Chip
      size="small"
      label={label}
      style={{
        color,
        backgroundColor: withAlpha(color, 0.13),
        border: `1px solid ${withAlpha(color, 0.3)}`,
        fontWeight: 600,
      }}
    />
  );
}

// ── Fechas (relojes de retención) ───────────────────────────────────────────
// El dato puede llegar como Timestamp de Firestore, {seconds}, Date, string o
// número. Lo normalizamos para mostrar y para contar días.
export function toDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  let d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDate(value: any): string {
  let d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
